// Minnesotaänger Mini App — message list scroller (v1 rewrite).
// The scroller/paging logic is v1 (telegram-tt's MessageList handled
// virtualization, read receipts and scroll-sync with an absolute composer
// footer, none of which v1 needs); the rendering chain underneath is the
// restored Web A one: mini-app messages are mapped through
// miniAppMessageToApiMessage, grouped by groupMessages (sender groups +
// day separators), and consumed by MessageListContent/Message with the
// original DOM contracts and SCSS.

import { memo, useLayoutEffect, useMemo, useRef } from '../../lib/teact/teact';

import type { ElementRef } from '../../lib/teact/teact';

import { getActions, withGlobal } from '../../global';

import type { ApiMiniAppMessage } from '../../api/miniapp/types';
import { miniAppMessageToApiMessage } from '../../api/miniapp/adapter';
import { groupMessages } from './helpers/groupMessages';

import {
  selectChatMessages,
  selectIsLoadingMessages,
  selectIsLoadingMore,
  selectOlderCursor,
  selectPendingOperations,
  selectSelectedMessageIds,
} from '../../global/selectors/miniapp';
import type { MiniAppPendingOperation } from '../../global/miniapp/types';
import useLastCallback from '../../hooks/useLastCallback';

import Loading from '../ui/Loading';

import MessageListContent from './MessageListContent';

import './MessageList.scss';

type OwnProps = {
  chatId: string;
  scrollerRef: ElementRef<HTMLDivElement>;
  onScrollDownToggle: (isNeeded: boolean) => void;
};

type StateProps = {
  messages: ApiMiniAppMessage[];
  olderCursor?: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  // Projected into every bubble: selection-mode tap targets and pending-op
  // disable state (§3.4/§3.5).
  selectedMessageIds: string[];
  pendingOperations: MiniAppPendingOperation[];
};

const LOAD_MORE_THRESHOLD_PX = 400;
const AT_BOTTOM_EPSILON_PX = 16;

function MessageList({
  chatId,
  scrollerRef,
  onScrollDownToggle,
  messages,
  olderCursor,
  isLoading,
  isLoadingMore,
  selectedMessageIds,
  pendingOperations,
}: OwnProps & StateProps) {
  const { loadMoreHistory } = getActions();

  const messageGroups = useMemo(() => {
    if (!messages.length) return [];
    return groupMessages(messages.map((message) => miniAppMessageToApiMessage(message, chatId)));
  }, [messages, chatId]);

  const isAtBottomRef = useRef(true);
  const prevScrollHeightRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  const prevChatIdRef = useRef<string>();
  const prevLengthRef = useRef<number>();

  const handleScroll = useLastCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const { scrollTop, scrollHeight, clientHeight } = scroller;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < AT_BOTTOM_EPSILON_PX;

    isAtBottomRef.current = isAtBottom;
    prevScrollHeightRef.current = scrollHeight;
    onScrollDownToggle(!isAtBottom);

    if (scrollTop < LOAD_MORE_THRESHOLD_PX && olderCursor != null && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;
      loadMoreHistory({ chatId });
    }
  });

  useLayoutEffect(() => {
    if (chatId !== prevChatIdRef.current) {
      prevChatIdRef.current = chatId;
      prevLengthRef.current = undefined;
      isAtBottomRef.current = true;
      isLoadingMoreRef.current = false;
    }

    const scroller = scrollerRef.current;
    if (!scroller) return;

    const { scrollHeight } = scroller;
    const len = messages.length;
    const prevLen = prevLengthRef.current;

    if (prevLen === undefined) {
      // First page (or chat switch): jump to the newest messages.
      if (len > 0) {
        scroller.scrollTop = scrollHeight;
      }
    } else if (isLoadingMoreRef.current && !isLoadingMore) {
      // Older messages were prepended (or the request failed): preserve the
      // viewport position. On failure the heights are equal, so this is a
      // no-op.
      scroller.scrollTop = scrollHeight - prevScrollHeightRef.current + scroller.scrollTop;
      isLoadingMoreRef.current = false;
    } else if (len > prevLen && isAtBottomRef.current) {
      // A new message arrived while at the bottom: follow it.
      scroller.scrollTop = scrollHeight;
    }
    // Same-length refresh (reroll/edit/delete): keep the position.

    prevLengthRef.current = len;
    prevScrollHeightRef.current = scrollHeight;
  }, [chatId, messages, isLoadingMore, scrollerRef]);

  const isEmpty = !isLoading && olderCursor == null && messages.length === 0;

  return (
    <div
      ref={scrollerRef}
      className="MessageList custom-scroll no-avatars"
      onScroll={handleScroll}
    >
      {isLoadingMore && (
        <div className="loading-more">正在加载更早的消息…</div>
      )}
      {isLoading && messages.length === 0 && (
        <div className="message-list-loading">
          <Loading />
        </div>
      )}
      {isEmpty && (
        <div className="empty-chat">还没有消息，先打个招呼吧</div>
      )}
      {messages.length > 0 && (
        <MessageListContent
          chatId={chatId}
          messageGroups={messageGroups}
          selectedMessageIds={selectedMessageIds}
          pendingOperations={pendingOperations}
        />
      )}
    </div>
  );
}

export default memo(withGlobal<OwnProps>((global, { chatId }): StateProps => ({
  messages: selectChatMessages(global, chatId),
  olderCursor: selectOlderCursor(global, chatId),
  isLoading: selectIsLoadingMessages(global, chatId),
  isLoadingMore: selectIsLoadingMore(global, chatId),
  selectedMessageIds: selectSelectedMessageIds(global, chatId),
  pendingOperations: selectPendingOperations(global, chatId),
}))(MessageList));
